import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MessageCircle, RefreshCw, CheckCircle2, XCircle, Clock, Copy, ExternalLink } from 'lucide-react';

const STATUS_STYLES = {
  APPROVED: { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  PENDING:  { icon: Clock,        cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  REJECTED: { icon: XCircle,      cls: 'bg-rose-100 text-rose-700 border-rose-200' },
};

// The event-template guide has moved to WhatsAppTemplateGuidePage.
// This page is strictly for managing / auditing the live CRM template list.
const EVENT_TEMPLATE_NAMES = [
  'eden_followup_confirmed',
  'eden_followup_confirmed_queue',
  'eden_payment_received',
  'eden_package_activated',
  'eden_package_visit_logged',
  'eden_reward_points_earned',
  'eden_reward_points_redeemed',
  'eden_feedback_ask_ameesha',
  'eden_feedback_ask_eden',
  'eden_feedback_thankyou_ameesha',
  'eden_feedback_thankyou_eden',
  'eden_admin_low_feedback',
  'eden_task_google_review',
  'eden_task_blog_comment',
  'eden_coupon_issued',
  'eden_admin_jobcard_opened',
  'eden_admin_jobcard_closed',
  'eden_admin_daily_summary',
  'eden_appointment_reminder',
  'eden_appointment_queue',
  'eden_appointment_missed',
];
const copy = (text) => {
  try { navigator.clipboard.writeText(text); toast.success('Copied'); } catch { toast.error('Copy failed'); }
};

export default function WhatsAppTemplatesPage({ user, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [errorSummary, setErrorSummary] = useState(null);
  const [sendSummary, setSendSummary] = useState(null);
  const [summaryHours, setSummaryHours] = useState(24);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get('/whatsapp/templates'),
        api.get('/whatsapp/config'),
      ]);
      setTemplates(t.data?.templates || []);
      setConfig(c.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const [logsRes, errRes, sumRes] = await Promise.all([
        api.get('/whatsapp/send-logs', { params: { limit: 30 } }),
        api.get('/whatsapp/error-summary', { params: { hours: 24 } }),
        api.get('/whatsapp/send-summary', { params: { hours: summaryHours } }),
      ]);
      setLogs(logsRes.data?.rows || []);
      setErrorSummary(errRes.data || null);
      setSendSummary(sumRes.data || null);
    } catch (e) {
      toast.error('Failed to load send logs');
    } finally {
      setLogsLoading(false);
    }
  }, [summaryHours]);

  useEffect(() => { load(); loadLogs(); }, [load, loadLogs]);

  const bodyOf = (tpl) => (tpl.components || []).find(c => c.type === 'BODY')?.text || '';
  const footerOf = (tpl) => (tpl.components || []).find(c => c.type === 'FOOTER')?.text || '';

  const templatesByName = Object.fromEntries(templates.map(t => [t.name, t]));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="wa-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-sm">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">WhatsApp Templates</h1>
              <p className="text-sm text-gray-500">Live list from Eden CRM · {templates.length} templates</p>
            </div>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="wa-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Provider config chip */}
        {config && (
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Provider</span>
            <span className="font-mono text-xs bg-slate-100 rounded-full px-2 py-0.5">{config.provider}</span>
            <span className="text-xs text-slate-500">{config.eden_crm?.base_url}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${config.eden_crm?.key_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
              {config.eden_crm?.key_configured ? 'Key OK' : 'Key missing'}
            </span>
          </div>
        )}

        {/* Recent sends log */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {errorSummary && errorSummary.total_failed > 0 && (
            <div className="bg-rose-50 border-b border-rose-100 px-4 py-2">
              <div className="text-xs font-semibold text-rose-800">Failures in the last {errorSummary.hours}h: {errorSummary.total_failed}</div>
              {errorSummary.by_template?.length > 0 && (
                <div className="text-[11px] text-rose-700 mt-0.5">
                  Templates:&nbsp;
                  {errorSummary.by_template.slice(0, 5).map((r, i) => (
                    <span key={i} className="mr-2"><span className="font-mono">{r.template}</span> ×{r.count}</span>
                  ))}
                </div>
              )}
              {errorSummary.by_error?.length > 0 && (
                <div className="text-[11px] text-rose-700 mt-0.5 line-clamp-2">
                  Top errors: {errorSummary.by_error.slice(0, 3).map((r, i) => (
                    <span key={i} className="mr-2">&ldquo;{String(r.error).slice(0, 40)}&hellip;&rdquo; ×{r.count}</span>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Per-template send counts — failures pinned on top */}
          <div className="border-b border-slate-100 px-4 py-3" data-testid="wa-send-summary">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-800">
                Templates sent · last {sendSummary?.hours || summaryHours}h
                {sendSummary && (
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {sendSummary.total_sent} sent{sendSummary.total_failed > 0 && <span className="text-rose-600 font-semibold"> · {sendSummary.total_failed} failed</span>}
                  </span>
                )}
              </div>
              <div className="flex gap-1">
                {[24, 72, 168].map((h) => (
                  <button
                    key={h}
                    onClick={() => setSummaryHours(h)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${summaryHours === h ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`wa-summary-hours-${h}`}
                  >
                    {h === 24 ? '24h' : h === 72 ? '3d' : '7d'}
                  </button>
                ))}
              </div>
            </div>
            {(!sendSummary || sendSummary.by_template?.length === 0) ? (
              <div className="text-xs text-slate-400 italic mt-2">No sends in this window.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {sendSummary.by_template.map((r) => (
                  <span
                    key={r.template}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-lg border ${r.failed > 0 ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}
                    data-testid={`wa-summary-${r.template}`}
                  >
                    {r.template}
                    <span className="font-sans font-semibold text-emerald-700">✓{r.sent}</span>
                    {r.failed > 0 && <span className="font-sans font-bold text-rose-700">✗{r.failed}</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="bg-slate-50 border-b border-slate-100 px-4 py-2.5 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">Recent WhatsApp Sends</div>
              <div className="text-xs text-slate-500">Last 30 event-driven sends · click a red row to see why it failed</div>
            </div>
            <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading} className="gap-2" data-testid="wa-logs-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Event</th>
                  <th className="text-left px-3 py-2 font-medium">Template</th>
                  <th className="text-center px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Result / Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && !logsLoading && (
                  <tr><td colSpan="5" className="text-center py-6 text-slate-400 italic">No sends recorded yet.</td></tr>
                )}
                {logs.map((l) => {
                  const okCls = l.success ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700';
                  const errText = String(l.error || '').slice(0, 220);
                  return (
                    <tr key={l.id} className="border-t border-slate-100" data-testid={`log-${l.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-500">{(l.sent_at || '').slice(11, 19)} <span className="text-slate-400">{(l.sent_at || '').slice(0, 10)}</span></td>
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">{l.event}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-slate-700">{l.template_name}</td>
                      <td className={`px-3 py-2 text-center font-semibold ${okCls} border-l border-r border-white`}>
                        {l.success ? '✓ Sent' : '✗ Failed'}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {l.success ? (
                          <span className="font-mono text-[11px]">wamid: {(l.message_id || '').slice(0, 32) || '—'}</span>
                        ) : (
                          <span className="text-rose-700 text-[11px]">{errText}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* System-triggered checklist */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-100 px-4 py-2.5">
            <div className="text-sm font-semibold text-slate-800">System-triggered templates</div>
            <div className="text-xs text-slate-500">Approved & live in Eden CRM · Missing ones need submission (see <b>WhatsApp Template Guide</b>).</div>
          </div>
          <div className="divide-y divide-slate-100">
            {EVENT_TEMPLATE_NAMES.map((name) => {
              const t = templatesByName[name];
              const status = t?.status || 'MISSING';
              const style = STATUS_STYLES[status] || { icon: XCircle, cls: 'bg-gray-100 text-gray-600 border-gray-200' };
              const Icon = style.icon;
              return (
                <div key={name} className="px-4 py-2.5 flex items-center gap-3" data-testid={`event-tpl-${name}`}>
                  <Icon className={`w-4 h-4 flex-shrink-0 ${status === 'APPROVED' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <span className="font-mono text-xs text-slate-800 flex-1">{name}</span>
                  <button onClick={() => copy(name)} className="text-slate-400 hover:text-slate-700" title="Copy name"><Copy className="w-3 h-3" /></button>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${style.cls}`}>{status}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Full CRM template list */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-100 px-4 py-2.5 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">All approved templates in Eden CRM</div>
              <div className="text-xs text-slate-500">Read-only mirror. Manage/submit new templates in the CRM UI.</div>
            </div>
            <a
              href={(config?.eden_crm?.base_url || 'https://crm.eden-office.in') + '/templates'}
              target="_blank" rel="noopener noreferrer"
              className="text-xs font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1"
            >
              Open CRM <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="divide-y divide-slate-100">
            {templates.length === 0 && !loading && (
              <div className="text-center py-8 text-sm text-slate-400 italic">No templates found.</div>
            )}
            {templates.map((t) => {
              const style = STATUS_STYLES[t.status] || { icon: XCircle, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
              return (
                <div key={t.name + t.language} className="px-4 py-3" data-testid={`tpl-row-${t.name}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-slate-900">{t.name}</span>
                    <Badge className="bg-slate-100 text-slate-700 text-[10px]">{t.language}</Badge>
                    <Badge className="bg-slate-100 text-slate-700 text-[10px]">{t.category}</Badge>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${style.cls}`}>{t.status}</span>
                    <button onClick={() => copy(t.name)} className="text-slate-400 hover:text-slate-700" title="Copy name"><Copy className="w-3 h-3" /></button>
                  </div>
                  {bodyOf(t) && (
                    <pre className="text-[12px] text-slate-700 whitespace-pre-wrap font-sans mt-2 bg-slate-50 rounded-lg p-2 border border-slate-100">{bodyOf(t)}</pre>
                  )}
                  {footerOf(t) && <div className="text-[10px] text-slate-500 mt-1">Footer: {footerOf(t)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
